import moment from "moment";
import { toast } from "react-toastify";
import { dispatchStatusEnum } from "./pages/private/order-management/util";

export const mapDataToLabelValue = (
  data: any[],
  labelKey: string,
  valueKey: string
): { label: string; value: string | number }[] => {
  return data?.map((item: any) => ({
    label: item[labelKey]?.toString() ?? "", // Convert item[labelKey] to a string for the label
    value: item[valueKey], // Use item[valueKey] for the value
  }));
};

export const mapDataToLabelValueForCourierOffices = (
  data: any[],
  labelKey: string,
  valueKey: string
): { label: string; value: string | number }[] => {
  return data?.map((item: any) => ({
    label: item[labelKey]?.toString() ?? "", // Convert item[labelKey] to a string for the label
    value: item[valueKey]?.id, // Use item[valueKey] for the value
  }));
};

export function formatNumberWithCommas(number: number) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    number
  );
}

export const copyToClipboard = (text: string): void => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success("Text copied to clipboard:");
    },
    (err) => {
      toast.error("Failed to copy text:");
    }
  );
};

export const getDaysLeft = (date: string): number => {
  const now = moment();
  const targetDate = moment(date);
  const difference = targetDate.diff(now, "days");
  return difference >= 0 ? difference : 0; // Return 0 if the date is in the past
};

export const getStatusesAfter = (status: string) => {
  // Find the index of the passed status in the array
  const statusIndex = dispatchStatusEnum?.findIndex(
    (item) => item.value === status
  );

  // If the status is not found or it's the last item, return an empty array
  if (statusIndex === -1 || statusIndex === dispatchStatusEnum?.length - 1) {
    return [];
  }

  // Slice the array from the index + 1 to the end
  return dispatchStatusEnum?.slice(statusIndex + 1);
};

export const cleanObject = (obj: Record<string, any>) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== null && value !== undefined && value !== ""
    )
  );
};

export const filterArray = <T>(array: T[], exclude: T[]): T[] => {
  return array?.filter((item) => !exclude?.includes(item));
};

export const nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ' -]{2,}$/;
export const phoneRegex = /^\+?[0-9\s\-().]{7,20}$/;
export const complexEmailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/;
export const EMAIL_REGEX = /^\w+([.-]?\w+)@\w+([.-]?\w+)(\.\w{2,3})+$/;
export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export const convertToSeconds = (value: number, unit: string): number => {
  switch (unit) {
    case "days":
      return value * 86400;
    case "hours":
      return value * 3600;
    case "seconds":
    default:
      return value;
  }
};

export const convertFromSeconds = (seconds: number, unit: string): number => {
  switch (unit) {
    case "days":
      return seconds / 86400;
    case "hours":
      return seconds / 3600;
    case "seconds":
    default:
      return seconds;
  }
};

export const convertToUnit = (seconds: number) => {
  if (seconds % 86400 === 0) return { value: seconds / 86400, unit: "days" };
  if (seconds % 3600 === 0) return { value: seconds / 3600, unit: "hours" };
  return { value: seconds, unit: "seconds" };
};


// role management
export type UserRole =
  | "COURIER_AGENT"
  | "COURIER_ADMIN"
  | "SUPER_ADMIN"
  | "DISPUTE_ADMIN"
  | "OPP_ADMIN"
  | "INVESTOR"
  | "UNKNOWN"; // fallback type

// General check
export const hasRole = (user: any, roles: UserRole[]) =>
  roles.includes(user?.user?.roles);

// Specific role groups
export const isCourierOrAdmin = (user: any) =>
  hasRole(user, [
    "COURIER_AGENT",
    "COURIER_ADMIN",
    "SUPER_ADMIN",
    "DISPUTE_ADMIN",
    "OPP_ADMIN",
    // "INVESTOR",
  ]);

export const isCourierAdminOrSuper = (user: any) =>
  hasRole(user, ["COURIER_ADMIN", "SUPER_ADMIN"]);

export const isInvestorOrSuper = (user: any) =>
  hasRole(user, ["INVESTOR", "SUPER_ADMIN"]);

export const isSuperAdmin = (user: any) => hasRole(user, ["SUPER_ADMIN"]);
